import { useFlags, useLDClient, type LDClient, type LDFlagSet } from '@launchdarkly/react-sdk';
import { useEffect } from 'react';
import { LD_FLAGS } from '@app/constants/launch-darkley';
import { useAppDispatch, useAppSelector, RootState } from './redux/store';
import { selectAppId, setLDflags, setLdIdentifyComplete } from './redux/slice/appSlice';
import { isAuthenticated, isAuthInitialized } from './utils/authUtils';
import { setSharedLdClient } from './utils/sharedLdClient';

function buildLdFlags(ldClient: LDClient): LDFlagSet {
  return {
    ...ldClient.allFlags(),
    [LD_FLAGS.AUTH_ONLY_CHECKOUT_ENABLED]: ldClient.variation(
      LD_FLAGS.AUTH_ONLY_CHECKOUT_ENABLED,
      false,
    ),
  };
}

function InitLD() {
  const ldFlags = useFlags();
  const ldClient = useLDClient();
  const appId = useAppSelector(selectAppId);
  const authContext = useAppSelector((state: RootState) => state.auth.authContext);
  const loginStatus = useAppSelector((state: RootState) => state.auth.loginStatus);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setSharedLdClient(ldClient ?? null);
  }, [ldClient]);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    dispatch(setLDflags(buildLdFlags(ldClient)));
  }, [dispatch, ldClient, ldFlags]);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    if (!isAuthInitialized(loginStatus)) {
      dispatch(setLdIdentifyComplete(false));
      return;
    }

    let cancelled = false;

    const runIdentify = async () => {
      dispatch(setLdIdentifyComplete(false));

      if (!appId) {
        return;
      }

      if (isAuthenticated(loginStatus) && authContext?.userId) {
        await ldClient.identify({
          kind: 'user',
          key: appId,
          email: authContext.email,
          companyId: authContext.companyId,
          userId: authContext.userId,
          anonymous: false,
        });
      } else {
        await ldClient.identify({
          kind: 'user',
          key: appId,
          anonymous: true,
        });
      }

      if (!cancelled) {
        dispatch(setLDflags(buildLdFlags(ldClient)));
        dispatch(setLdIdentifyComplete(true));
      }
    };

    void runIdentify();

    return () => {
      cancelled = true;
    };
  }, [ldClient, loginStatus, authContext, appId, dispatch]);

  return <></>;
}
export default InitLD;
